/**
 * Settings › AI in the console (spec 2026-10-11-ai-operations-settings): the
 * two reads (`worker/ai-settings.ts`, over the ADMIN_READS RPC), the one write
 * (`PUT /api/admin/ai/operations/:operation`, session only, its own CSRF
 * header) and the small pure rules the page shares: what a row's draft
 * changed, what a reset sends, which saves ask first on production, and how
 * money and counts read.
 *
 * The API Worker validates every change again against the registry and the
 * models the AI Gateway serves; `validateOperationUpdate` runs here first only
 * so a row can say what is wrong without a round trip.
 */
import {
  AI_OPERATIONS,
  AI_PROVIDER_LABELS,
  aiProviderOf,
  isWorkersAiModel,
  modelAllowedFor,
  servedOverAiService,
  type AiOperationArea,
  type AiOperationDefinition,
  type AiOperationMode,
  type AiOperationSettingsView,
  type AiOperationUpdate,
  type AiSettingsView,
  type AiUsageSummary,
  type AiUsageTotals,
} from "@nt/ai-contract";

import { apiGet } from "@/lib/api";
import { AGGREGATE, DETAIL } from "@/lib/query-freshness";

export const AI_SETTINGS_PATH = "/api/admin/ai/settings";
export const AI_USAGE_PATH = "/api/admin/ai/usage";

/** The spend windows the Worker accepts (`AI_USAGE_DAYS`). */
export const AI_USAGE_WINDOWS = [7, 30, 90] as const;
export type AiUsageDays = (typeof AI_USAGE_WINDOWS)[number];

const DEFAULT_SPEND_WINDOW: AiUsageDays = 30;

/** The spend window the address names, or the default when it names none the Worker accepts. */
export function spendWindowOf(params: URLSearchParams): AiUsageDays {
  const days = Number(params.get("days"));
  return (AI_USAGE_WINDOWS as readonly number[]).includes(days)
    ? (days as AiUsageDays)
    : DEFAULT_SPEND_WINDOW;
}

/** The address after a tab click: the other tab's choice stays, and a default stays out of it. */
export function aiSettingsParams(
  current: URLSearchParams,
  change: { view?: "models" | "spend"; days?: AiUsageDays },
): URLSearchParams {
  const params = new URLSearchParams(current);
  if (change.view === "models") params.delete("view");
  else if (change.view) params.set("view", change.view);
  if (change.days === DEFAULT_SPEND_WINDOW) params.delete("days");
  else if (change.days) params.set("days", String(change.days));
  return params;
}

export function aiSettingsQuery() {
  return {
    ...DETAIL,
    queryKey: ["ai-settings"] as const,
    queryFn: () => apiGet<AiSettingsView>(AI_SETTINGS_PATH),
  };
}

export function aiUsageQuery(days: AiUsageDays) {
  return {
    ...AGGREGATE,
    queryKey: ["ai-usage", days] as const,
    queryFn: () => apiGet<AiUsageSummary>(AI_USAGE_PATH, { days }),
  };
}

export interface AiWriteFailure extends Error {
  status: number;
  code: string | null;
}

/** Same-origin PUT with the session cookie and the custom header the Worker demands. */
export async function saveAiOperation(
  operation: string,
  update: AiOperationUpdate,
): Promise<unknown> {
  const response = await fetch(
    `/api/admin/ai/operations/${encodeURIComponent(operation)}`,
    {
      method: "PUT",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Admin-Ai-Settings-Write": "1",
      },
      body: JSON.stringify(update),
    },
  );
  const result = (await response.json().catch(() => null)) as {
    data?: unknown;
    error?: { code?: string; message?: string };
  } | null;
  if (!response.ok) {
    throw Object.assign(
      new Error(
        result?.error?.message ?? `Request failed (${response.status}).`,
      ),
      { status: response.status, code: result?.error?.code ?? null },
    ) as AiWriteFailure;
  }
  return result?.data ?? null;
}

/** What an operator reads when a save fails: the reason, and what to do next. */
export function aiWriteFailureMessage(error: unknown): string {
  const failure = error as Partial<AiWriteFailure> | null;
  switch (failure?.status) {
    case 401:
      return "Your session ended. Sign in again, then save the row again.";
    case 403:
      return "The console refused this save. Reload the page, sign in with your own account (not an API key) and try again.";
    case 404:
      return "This operation is not known here. Reload the page; if it is still listed, deploy the console and nt-api together.";
    case 400:
    case 409:
      return `${failure.message ?? "The API refused this change."} Nothing was saved. Fix the row and save again.`;
    default:
      return `${failure?.message ?? "The save failed."} Reload the page to check what is saved, then try again.`;
  }
}

// ------------------------------------------------------------------ payloads

/**
 * The settings read with every list present: payload shapes are the API
 * Worker's, so a missing field blanks a part of the page, never breaks it.
 */
export function normalizeSettings(raw: Partial<AiSettingsView> | undefined) {
  return {
    operations: Array.isArray(raw?.operations) ? raw.operations : [],
    servedModels: Array.isArray(raw?.servedModels) ? raw.servedModels : null,
    servedModelsError:
      typeof raw?.servedModelsError === "string" ? raw.servedModelsError : null,
  } satisfies AiSettingsView;
}

const ZERO_TOTALS: AiUsageTotals = {
  calls: 0,
  failures: 0,
  inputTokens: 0,
  outputTokens: 0,
  billedCostMicros: 0,
  listCostMicros: 0,
  callsWithoutListPrice: 0,
};

/** The spend read with totals and every breakdown present (see `normalizeSettings`). */
export function normalizeUsage(
  raw: Partial<AiUsageSummary> | undefined,
  days: number,
): AiUsageSummary {
  return {
    days: typeof raw?.days === "number" ? raw.days : days,
    from: typeof raw?.from === "string" ? raw.from : "",
    to: typeof raw?.to === "string" ? raw.to : "",
    totals: { ...ZERO_TOTALS, ...(raw?.totals ?? {}) },
    byOperation: Array.isArray(raw?.byOperation) ? raw.byOperation : [],
    byModel: Array.isArray(raw?.byModel) ? raw.byModel : [],
    byProvider: Array.isArray(raw?.byProvider) ? raw.byProvider : [],
    byDay: Array.isArray(raw?.byDay) ? raw.byDay : [],
  };
}

// ------------------------------------------------------------------ drafts

export interface OperationDraft {
  mode: AiOperationMode;
  primaryModel: string;
  fallbackModel: string | null;
}

export function draftOf(view: AiOperationSettingsView): OperationDraft {
  return {
    mode: view.mode,
    primaryModel: view.primaryModel,
    fallbackModel: view.fallbackModel,
  };
}

/** Only the keys the draft changed, or null when nothing did. */
export function changedUpdate(
  view: AiOperationSettingsView,
  draft: OperationDraft,
): AiOperationUpdate | null {
  const update: AiOperationUpdate = {};
  if (draft.mode !== view.mode) update.mode = draft.mode;
  if (draft.primaryModel !== view.primaryModel)
    update.primaryModel = draft.primaryModel;
  if (draft.fallbackModel !== view.fallbackModel)
    update.fallbackModel = draft.fallbackModel;
  return Object.keys(update).length > 0 ? update : null;
}

/** `null` for every saved value (back to the registry default), or null when all are defaults. */
export function resetUpdate(
  view: AiOperationSettingsView,
): AiOperationUpdate | null {
  const update: AiOperationUpdate = {};
  if (view.source.mode === "saved") update.mode = null;
  if (view.source.primaryModel === "saved") update.primaryModel = null;
  if (view.source.fallbackModel === "saved") update.fallbackModel = null;
  return Object.keys(update).length > 0 ? update : null;
}

export function isAllDefault(view: AiOperationSettingsView): boolean {
  return resetUpdate(view) === null;
}

export const MODE_LABEL: Record<AiOperationMode, string> = {
  on: "On",
  off: "Off",
  shadow: "Shadow",
  enforce: "Enforce",
};

/** The dropdown's option text: short enough to read in a narrow trigger. */
export const MODE_OPTION: Record<AiOperationMode, string> = {
  on: "On",
  off: "Off (no model call)",
  shadow: "Shadow (logs only)",
  enforce: "Enforce (acts on it)",
};

/**
 * What a production save asks the admin to check first: turning an operation
 * on (it starts spending and acting) and a model change (answers may differ).
 * Staging never asks.
 */
export function changesToConfirm(
  environment: string | undefined,
  op: AiOperationDefinition,
  update: AiOperationUpdate,
): string[] {
  if (environment !== "production") return [];
  const changes: string[] = [];
  const mode = update.mode === null ? op.defaultMode : update.mode;
  if (mode !== undefined && mode !== "off") {
    changes.push(
      `${op.label} on production moves to ${MODE_LABEL[mode]}: every ${op.trigger.charAt(0).toLowerCase()}${op.trigger.slice(1)} starts a paid model call.`,
    );
  }
  if (update.primaryModel !== undefined || update.fallbackModel !== undefined) {
    changes.push(
      `${op.label} on production switches model on its next call. Watch its failure rate on the Spend tab afterwards.`,
    );
  }
  return changes;
}

const MODEL_NAMES: Readonly<Record<string, string>> = {
  "claude-haiku-5-5": "Haiku 5.5",
  "claude-sonnet-5-5": "Sonnet 5.5",
  "claude-sonnet-5-5-high": "Sonnet 5.5 High",
  "claude-opus-5-5": "Opus 5.5",
  "claude-opus-5-5-high": "Opus 5.5 High",
  "claude-fable-5-1": "Fable 5.1",
  "claude-haiku-4-5-20251001": "Haiku 4.5",
  "gemini-3.8-flash-high": "Gemini 3.8 Flash",
  "gemini-3.5-flash-lite": "Gemini 3.5 Flash-Lite",
  "gemini-3.1-flash-lite": "Gemini 3.1 Flash-Lite",
  "gemini-4-argon-high": "Gemini 4 Argon",
  "gpt-6-luna": "GPT-6 Luna",
  "gpt-6-astra": "GPT-6 Astra",
  "gpt-6-sol": "GPT-6 Sol",
  "gpt-6.1-sol": "GPT-6.1 Sol",
  "gpt-6.1-terra": "GPT-6.1 Terra",
  "glm-5.3": "GLM 5.3",
  "glm-5.3-flash": "GLM 5.3 Flash",
  "glm-5.3-flashx": "GLM 5.3 FlashX",
  "@cf/moonshotai/kimi-k2.6": "Kimi K2.6",
  "@cf/zai-org/glm-5.3-flash": "GLM 5.3 Flash",
  "@cf/google/embeddinggemma-300m": "EmbeddingGemma",
};

/** A model as staff read it: "Haiku 5.5", whichever provider serves it. An unknown id reads as sent. */
export function modelName(model: string): string {
  return MODEL_NAMES[model.replace(/^anthropic\//, "")] ?? model;
}

/**
 * The name, with its provider when that is not the default one. The same model
 * can sit twice in a chain (CLIProxyAPI, then the billed Anthropic API), and
 * the two must read differently.
 */
export function modelLabel(model: string): string {
  const provider = aiProviderOf(model);
  return provider === "cliproxy"
    ? modelName(model)
    : `${modelName(model)} (${AI_PROVIDER_LABELS[provider]})`;
}

const MODEL_GROUPS = [
  { provider: "cliproxy", label: "CLIProxyAPI, no per-call charge" },
  { provider: "anthropic", label: "Anthropic API, billed per call" },
  { provider: "workers-ai", label: "Workers AI, billed per call" },
] as const;

/** Claude first in every group: it is what the operations mostly run on. */
function pickerRank(model: string): number {
  const bare = model.replace(/^anthropic\//, "");
  if (bare === "claude-haiku-5-5") return 0;
  if (bare === "claude-sonnet-5-5") return 1;
  return 2;
}

/**
 * What one operation's model picker offers, by who serves and bills it: only
 * models this operation can run (`validateOperationUpdate` refuses the rest), plus the
 * value in effect when it is not one of them, so the trigger can still name it.
 */
export function modelOptionGroups(
  op: AiOperationDefinition,
  served: readonly string[],
  current: string | null,
): Array<{
  label?: string;
  options: Array<{ value: string; label: string }>;
}> {
  const runnable = served.filter(
    (model) =>
      modelAllowedFor(op, model) &&
      (!isWorkersAiModel(model) || servedOverAiService(op)),
  );
  const groups: Array<{
    label?: string;
    options: Array<{ value: string; label: string }>;
  }> = [];
  if (current && !runnable.includes(current)) {
    const why = served.includes(current) ? "cannot run here" : "not served now";
    groups.push({
      options: [{ value: current, label: `${modelLabel(current)} (${why})` }],
    });
  }
  for (const group of MODEL_GROUPS) {
    const options = runnable
      .filter((model) => aiProviderOf(model) === group.provider)
      .sort((a, b) => pickerRank(a) - pickerRank(b) || a.localeCompare(b))
      // The trigger shows only this label, so it says which route the model is on.
      .map((model) => ({ value: model, label: modelLabel(model) }));
    if (options.length > 0) groups.push({ label: group.label, options });
  }
  return groups;
}

// ------------------------------------------------------------------ grouping

export interface AreaGroup {
  area: AiOperationArea;
  rows: Array<{ op: AiOperationDefinition; view: AiOperationSettingsView }>;
}

/**
 * The registry's operations in registry order, grouped by area in the order
 * the areas first appear. An operation the API did not return (an older API
 * Worker) is left out rather than shown with invented values.
 */
export function groupByArea(
  views: readonly AiOperationSettingsView[],
): AreaGroup[] {
  const byId = new Map(views.map((view) => [view.id as string, view]));
  const groups: AreaGroup[] = [];
  for (const op of AI_OPERATIONS as readonly AiOperationDefinition[]) {
    const view = byId.get(op.id);
    if (!view) continue;
    let group = groups.find((entry) => entry.area === op.area);
    if (!group) {
      group = { area: op.area, rows: [] };
      groups.push(group);
    }
    group.rows.push({ op, view });
  }
  return groups;
}

const LABEL_BY_ID = new Map<string, string>(
  (AI_OPERATIONS as readonly AiOperationDefinition[]).map((op) => [
    op.id,
    op.label,
  ]),
);

/** An operation's label for the spend tables; an id the registry no longer has reads as sent. */
export function operationLabel(id: string): string {
  return LABEL_BY_ID.get(id) ?? id;
}

// ------------------------------------------------------------------ spend

/** Micro-USD as dollars: "$0", "<$0.01", "$1.24", "$1,204". */
export function formatUsd(micros: number): string {
  if (!Number.isFinite(micros) || micros <= 0) return "$0";
  const dollars = micros / 1_000_000;
  if (dollars < 0.01) return "<$0.01";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: dollars < 100 ? 2 : 0,
  }).format(dollars);
}

/** 1,284 → "1,284"; 12,900 → "12.9K"; 4,200,000 → "4.2M". */
export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return "0";
  if (Math.abs(value) < 10_000) return value.toLocaleString("en-US");
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/** "2.5%" of calls that failed, or "—" with no calls. */
export function failureRate(totals: AiUsageTotals): string {
  if (totals.calls <= 0) return "—";
  const rate = (totals.failures / totals.calls) * 100;
  if (rate > 0 && rate < 0.1) return "<0.1%";
  return `${rate.toFixed(rate < 10 ? 1 : 0)}%`;
}

/** "2026-10-03" → "Oct 3". */
export function formatDay(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return day;
  return new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  ).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Every day of the window, oldest first, with a zero row for a day without
 * calls: a missing day must read as a gap in the bars, not be skipped.
 */
export function fillDays(summary: AiUsageSummary): AiUsageSummary["byDay"] {
  const byDay = new Map(summary.byDay.map((row) => [row.day, row]));
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(summary.to);
  if (!match) return summary.byDay;
  const end = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  const days: AiUsageSummary["byDay"] = [];
  for (let index = summary.days - 1; index >= 0; index -= 1) {
    const day = new Date(end - index * 86_400_000).toISOString().slice(0, 10);
    days.push(byDay.get(day) ?? { day, ...ZERO_TOTALS });
  }
  return days;
}
